import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import SharedReportGrid from './SharedReportGrid.jsx';

// ── Preview & arrange ─────────────────────────────────────────────────────────
// What the reader gets, rendered by the SAME component the public page uses and
// fed by the same live queries — so this is the article, not an impression of it.
//
// Arrange mode turns on drag + resize. The arrangement is saved ON THE LINK, so a
// sponsor report can lead with the headline numbers while the dashboard everyone
// else uses is untouched. "Follow the dashboard again" clears the override, and
// the link goes back to inheriting the dashboard's layout — including future
// changes to it.
export default function SharePreview({ scope, link, onClose, backLabel = '← Back to links' }) {
  const isMobile = useIsMobile();
  const apiScope = scope === 'my' ? 'my' : 'admin-client';
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [arranging, setArranging] = useState(false);
  const [pending, setPending] = useState(null); // unsaved arrangement
  const [status, setStatus] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadReport = useCallback(() => {
    api.shareReportPreview(apiScope, link.id).then(setReport).catch((e) => setError(e.message));
  }, [apiScope, link.id]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const loadTile = useCallback(
    (tileId) => api.shareReportPreviewTile(apiScope, link.id, tileId),
    [apiScope, link.id],
  );

  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(''), 1800); };
  const save = async () => {
    try {
      await api.shareReportUpdate(apiScope, link.id, { layoutOverrides: pending });
      setPending(null); setArranging(false); flash('Layout saved ✓'); loadReport();
    } catch (e) { flash(e.message); }
  };
  const reset = async () => {
    if (!window.confirm('Go back to the dashboard\'s own layout for this link?')) return;
    try {
      await api.shareReportUpdate(apiScope, link.id, { layoutOverrides: {} });
      setPending(null); setArranging(false); flash('Following the dashboard again'); loadReport();
    } catch (e) { flash(e.message); }
  };

  // While arranging, render the unsaved arrangement so tiles stay where dropped.
  const tiles = (report?.tiles || []).map((t) => (pending?.[t.id] ? { ...t, layout: pending[t.id] } : t));
  const carousels = (report?.carousels || []).map((c) => (pending?.[c.id] ? { ...c, layout: pending[c.id] } : c));
  const arranged = !!pending && Object.keys(pending).length > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <button style={back} onClick={onClose}>{backLabel}</button>
        <span style={{ flex: 1 }} />
        {status && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{status}</span>}
        <button style={mini} onClick={() => setRefreshKey((k) => k + 1)}>Refresh data</button>
        {!isMobile && (arranging ? (
          <>
            <button style={{ ...primary, padding: '8px 14px', minHeight: 38, opacity: arranged ? 1 : 0.55 }} disabled={!arranged} onClick={save}>Save layout</button>
            <button style={mini} onClick={() => { setPending(null); setArranging(false); }}>Cancel</button>
          </>
        ) : (
          <button style={mini} onClick={() => setArranging(true)}>Arrange layout</button>
        ))}
        <button style={mini} onClick={reset}>Follow the dashboard again</button>
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '0 0 12px' }}>
        {isMobile
          ? 'This is what your reader sees on a phone. Rearranging is desktop-only — the phone view follows the desktop order.'
          : arranging
            ? 'Drag tiles to move them, drag a corner to resize. This changes THIS LINK only — the dashboard itself is untouched.'
            : 'Exactly what your reader sees, with live numbers. It does not count as an open.'}
      </p>

      {error && <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 10 }}>{error}</div>}
      {!report ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading the preview…</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: isMobile ? 10 : 14, background: 'var(--bg)' }}>
          <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{report.title}</h2>
          {report.note && <p style={{ marginTop: 6, marginBottom: 12, fontSize: 13.5, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{report.note}</p>}
          <SharedReportGrid
            tiles={tiles}
            carousels={carousels}
            load={loadTile}
            refreshKey={refreshKey}
            isMobile={isMobile}
            editable={arranging && !isMobile}
            onLayoutChange={setPending}
          />
        </div>
      )}
    </div>
  );
}


const primary = { padding: '10px 16px', minHeight: 42, borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const mini = { padding: '7px 11px', minHeight: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const back = { background: 'none', border: 'none', color: 'var(--brand)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 };
